import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Tooltip
} from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { MDBox, MDTypography, MDInput, MDButton } from '@components/index';
import { THEME_STYLES, THEME_LABELS } from '@constants/theme';
import { sendAmplitudeData } from '@utils/amplitude';
import { CHANGED_THEME } from '@constants/eventNames';
import { RootState } from '@store/store';
import { getChartTheme } from '@utils/chart';
import { ChartConfigData } from '@api/chart';

interface Props {
  setSelectedTheme: (colors: string[] | null) => void;
  isProjectBoard?: boolean;
}

const SelectThemeMenu: React.FC<Props> = ({
  setSelectedTheme,
  isProjectBoard = false
}) => {
  const projectBoard = useSelector((state: RootState) => state.projectBoard);
  const currentChartData = useSelector(
    (state: RootState) => state.currentChartData
  );

  const projectColorSet = projectBoard.currentProjectData?.colorset || null;

  // Selected theme in project/chart level theme pallete
  const selectedTheme = useMemo(
    () =>
      isProjectBoard
        ? projectColorSet
        : getChartTheme(currentChartData as unknown as ChartConfigData),
    [currentChartData?.colorset, currentChartData?.project_colorset]
  );

  const [appliedTheme, setAppliedTheme] = useState(
    Object.keys(THEME_STYLES).find(
      (key: string) =>
        THEME_STYLES[key as string]?.join() === selectedTheme?.join()
    ) || ''
  );
  const [customText, setCustomText] = useState('');
  const [customColors, setCustomColors] = useState<string[]>([]);
  const [customTextError, setCustomTextError] = useState('');

  const getThemeKey = (theme: string[]) =>
    Object.keys(THEME_STYLES).find(
      (key: string) => THEME_STYLES[key]?.join() === theme?.join()
    );
  const handleThemeSelection = useDebouncedCallback(() => {
    if (appliedTheme === 'custom') {
      if (customText) {
        const { cleanedColorsArr, hasError } = getCleanedColors();

        if (hasError) {
          setCustomTextError(hasError);
        } else {
          setCustomTextError('');
          sendAmplitudeData(CHANGED_THEME, { 'Theme Name': 'Custom' });

          setSelectedTheme(cleanedColorsArr);
        }
      } else {
        setCustomTextError(
          'Please enter at-least one HEX code and then click OK.'
        );
      }
    } else {
      setSelectedTheme(THEME_STYLES[appliedTheme]);
      sendAmplitudeData(CHANGED_THEME, {
        'Theme Name': THEME_LABELS[appliedTheme]
      });
      setCustomTextError('');
    }
  }, 200);

  const getColorArr = useDebouncedCallback(() => {
    if (customText) {
      const { cleanedColorsArr } = getCleanedColors();

      setCustomColors(cleanedColorsArr.slice(0, 3));
    } else {
      setCustomColors([]);
    }
    setCustomTextError('');
  }, 300);

  const getCleanedColors = (): {
    cleanedColorsArr: string[];
    hasError: string;
  } => {
    const selectedColorsArr = customText.split(',');

    const cleanedColorsArr: string[] = [];
    let hasError = '';

    if (selectedColorsArr.length > 5) hasError = 'Max 5 HEX codes allowed';
    else
      selectedColorsArr.forEach((colorStr: string) => {
        let color = colorStr.trim();

        if (color)
          if (color.match(/^((#?)+(?:[0-9a-fA-F]{3}){1,2})$/)) {
            if (color[0] !== '#') color = `#${color}`;
            cleanedColorsArr.push(color);
          } else {
            hasError = 'Please enter valid HEX codes';
          }
      });

    return { cleanedColorsArr, hasError };
  };

  const selectedThemeLabel = useMemo(() => {
    const labelKey = getThemeKey(THEME_STYLES[appliedTheme]);

    return labelKey ? THEME_LABELS[labelKey] : 'Custom';
  }, [appliedTheme]);

  useEffect(() => {
    getColorArr();
  }, [customText]);

  useEffect(() => {
    if (selectedTheme?.length) {
      const selectedThemeKey = getThemeKey(selectedTheme);

      if (selectedThemeKey) {
        setAppliedTheme(selectedThemeKey);
      } else {
        setCustomText(selectedTheme?.join(','));
        setAppliedTheme('custom');
      }
    } else if (!selectedTheme) {
      setAppliedTheme('DEFAULT');
    }
  }, [selectedTheme]);

  const themesOptions = Object.keys(THEME_STYLES);

  return (
    <MDBox
      minWidth='95%'
      maxWidth='95%'
      px={1}
      py={2}
      mb={2}
      bordered
      borderRadius='16px'
      my='16px'
      position='relative'
      sx={({ palette }) => ({
        borderColor: palette.info.main
      })}
    >
      <MDBox
        justifyContent='space-between'
        display='flex'
        flexWrap='nowrap'
        alignItems='flex-start'
      >
        <MDBox>
          <MDTypography variant='h5' color='dark' mb={2}>
            Theme currently applied:
            <br />
            {` ${selectedThemeLabel}`}
          </MDTypography>
        </MDBox>
        <MDBox>
          <MDButton
            color='primary'
            variant='gradient'
            circular={false}
            sx={{ marginTop: '8px', px: '16px' }}
            onClick={handleThemeSelection}
          >
            Ok
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox px={1}>
        <MDBox mb={2}>
          <RadioGroup
            name='theme-radio'
            value={appliedTheme || 'default'}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setAppliedTheme((event.target as HTMLInputElement).value);
              setCustomTextError('');
            }}
          >
            {themesOptions?.map((theme) => (
              <Grid
                container
                key={theme}
                alignItems='center'
                justifyContent='space-between'
              >
                <Grid item>
                  <FormControlLabel
                    value={theme}
                    control={<Radio color='secondary' />}
                    label={THEME_LABELS[theme]}
                    checked={theme === appliedTheme}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Grid>
                <Grid container item width={'auto'}>
                  {THEME_STYLES[theme]?.slice(0, 3)?.map((optn) => (
                    <MDBox
                      key={optn}
                      sx={{
                        backgroundColor: `${optn}`,
                        width: '20px',
                        height: '30px'
                      }}
                      bordered
                    />
                  ))}
                </Grid>
              </Grid>
            ))}
          </RadioGroup>
        </MDBox>
        <Grid container justifyContent='space-between' pb='10px'>
          <Divider variant={'fullWidth'} sx={{ opacity: 1, flex: 1 }} />

          <MDTypography
            variant='xl'
            color='info'
            px={2}
            sx={{ lineHeight: 'normal' }}
          >
            OR
          </MDTypography>
          <Divider variant={'fullWidth'} sx={{ opacity: 1, flex: 1 }} />
        </Grid>
        <MDTypography variant='lg' color='dark'>
          Create your own
        </MDTypography>
        <MDTypography variant='xs' color='info' my={2} component={MDBox}>
          Use comma to separate HEX codes
        </MDTypography>
        <Grid container justifyContent='space-between' alignItems='center'>
          <Grid container item flex={1} maxWidth={'230px'}>
            <Grid item>
              <Radio
                color='secondary'
                checked={appliedTheme === 'custom'}
                onChange={(event, value) => {
                  if (value) setAppliedTheme('custom');
                }}
              />
            </Grid>
            <Grid item flex={1}>
              <Tooltip
                title='Upto 5 HEX codes separated by comma can be inputted here'
                placement='top'
              >
                <MDInput
                  value={customText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setAppliedTheme('custom');
                    setCustomText(e.target.value);
                  }}
                  error={customTextError}
                  placeholder='HEX codes e.g. #10A37F'
                  sx={{ backgroundImage: 'none' }}
                />
              </Tooltip>
            </Grid>
          </Grid>
          <Grid container item width={'auto'}>
            {customColors.map((color: string, index: number) => (
              <MDBox
                key={index}
                bordered
                sx={() => ({
                  backgroundColor: color,
                  width: '20px',
                  height: '30px'
                })}
              />
            ))}
          </Grid>
        </Grid>
        <MDTypography
          color='error'
          variant='xxs'
          py={1}
          ml={5}
          component='div'
          sx={{
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            maxWidth: '70%'
          }}
        >
          {customTextError}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
};

export default SelectThemeMenu;
